import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthStore } from '../store/auth.store';
import { CompanyContextStore } from '../store/company-context.store';
import { StorageService } from './storage.service';

export type SessionEndReason = 'inactivity' | 'session_expired' | 'no_role';

/** Motivos que se explican con un aviso antes de llevar al usuario al login. */
const REASONS_WITH_NOTICE: SessionEndReason[] = ['inactivity', 'session_expired'];

/**
 * Punto único de cierre de sesión. Separa el momento en que se invalidan las
 * credenciales del momento en que se abandona la pantalla, para que el usuario
 * nunca sea arrancado de un formulario sin explicación.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private router     = inject(Router);
  private authStore  = inject(AuthStore);
  private storage    = inject(StorageService);
  private companyCtx = inject(CompanyContextStore);

  private readonly reason = signal<SessionEndReason | null>(null);
  private returnUrl: string | null = null;

  /** Motivo pendiente de confirmar por el usuario; `null` si no hay aviso activo. */
  readonly pendingNotice = this.reason.asReadonly();

  end(reason: SessionEndReason): void {
    if (!this.authStore.isAuthenticated() || this.reason() !== null) return;

    this.returnUrl = this.currentAppUrl();
    this.clear();

    if (REASONS_WITH_NOTICE.includes(reason)) {
      this.reason.set(reason);
      return;
    }
    this.goToLogin(reason);
  }

  /** El usuario confirmó el aviso: recién ahora se navega al login. */
  acknowledge(): void {
    const reason = this.reason();
    this.reason.set(null);
    if (reason) this.goToLogin(reason);
  }

  /** Cierre voluntario desde el menú: sin aviso ni retorno posterior. */
  logout(): void {
    this.returnUrl = null;
    this.reason.set(null);
    this.clear();
    this.router.navigate(['/auth/sing-in']);
  }

  private clear(): void {
    this.companyCtx.exit();
    this.authStore.clearSession();
    this.storage.clearSession();
  }

  private goToLogin(reason: SessionEndReason): void {
    const queryParams: Record<string, string> = { reason };
    if (this.returnUrl) queryParams['returnUrl'] = this.returnUrl;
    this.returnUrl = null;
    this.router.navigate(['/auth/sing-in'], { queryParams });
  }

  private currentAppUrl(): string | null {
    const url = this.router.url;
    return url.startsWith('/app') ? url : null;
  }
}
